import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDevices, useFilteredDevicesCount } from "../api/client";
import { DeviceFilters } from "../types/filters";
import { Device } from "../types/device";

const DEVICES_PER_PAGE = 15;
const CHUNK_SIZE = 50;

export function usePhoneFilters() {
    const [searchParams, setSearchParams] = useSearchParams();
    const filterRef = useRef<HTMLDivElement>(null);

    // --- Helper: Serialize Filters for URL ---
    const syncFiltersToUrl = (filters: DeviceFilters, page: number) => {
        const params = new URLSearchParams();
        params.set("page", page.toString());
        if (filters.q) params.set("q", filters.q);

        // Brands and Strings
        const stringArrKeys: (keyof DeviceFilters)[] = ["brands", "memory_slot", "sim_type", "biometrics"];
        stringArrKeys.forEach(key => {
            const val = filters[key] as string[];
            if (val?.length) params.set(key, val.join(","));
        });

        // Ranges
        const rangeKeys: (keyof DeviceFilters)[] = ["screen_size", "ram", "storage", "refresh_rate", "release_year", "main_camera", "front_camera"];
        rangeKeys.forEach(key => {
            const ranges = filters[key] as number[][];
            if (ranges?.length) params.set(key, ranges.map(r => r.join("-")).join(","));
        });

        // Booleans
        const boolKeys: (keyof DeviceFilters)[] = ["has_5g", "has_nfc", "fast_charging", "wireless_charging"];
        boolKeys.forEach(key => {
            if (filters[key]) params.set(key, "true");
        });

        setSearchParams(params);
    };

    // --- Helper: Parse Filters from URL (on Page Load) ---
    const parseUrlToFilters = (): DeviceFilters => {
        const filters: DeviceFilters = { q: searchParams.get("q") || "" };

        // Strings
        ["brands", "memory_slot", "sim_type", "biometrics"].forEach(key => {
            const val = searchParams.get(key);
            if (val) (filters as any)[key] = val.split(",");
        });

        // Ranges
        const rangeKeys: (keyof DeviceFilters)[] = ["screen_size", "ram", "storage", "refresh_rate", "release_year", "main_camera", "front_camera"];
        rangeKeys.forEach(key => {
            const val = searchParams.get(key);
            if (val) (filters as any)[key] = val.split(",").map(r => r.split("-").map(Number));
        });

        // Booleans
        ["has_5g", "has_nfc", "fast_charging", "wireless_charging"].forEach(key => {
            if (searchParams.get(key) === "true") (filters as any)[key] = true;
        });

        return filters;
    };
    // --- State Management ---
    const [allFetchedDevices, setAllFetchedDevices] = useState<Device[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const initialPage = parseInt(searchParams.get("page") || "0");
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [currentChunk, setCurrentChunk] = useState(() => Math.floor((initialPage * DEVICES_PER_PAGE) / CHUNK_SIZE));

    const [activeFilters, setActiveFilters] = useState<DeviceFilters>(parseUrlToFilters);
    const [pendingFilters, setPendingFilters] = useState<DeviceFilters>(activeFilters);

    // UI State
    const [popoverPos, setPopoverPos] = useState<{ top: number; right: number } | null>(null);
    const [closePopover, setClosePopover] = useState(true);

    const { data: chunkData, isLoading, error, isPlaceholderData } = useDevices(activeFilters, currentChunk);
    const { data: countData } = useFilteredDevicesCount(pendingFilters);

    // Sync TanStack to Buffer
    useEffect(() => {
        if (chunkData?.devices && !isPlaceholderData) {
            setAllFetchedDevices(prev => {
                let updatedList = [...prev];
                const insertAt = currentChunk * CHUNK_SIZE;
                // Remove old devices for this chunk to prevent leftover items
                for (let i = 0; i < CHUNK_SIZE; i++) {
                    delete updatedList[insertAt + i];
                }
                chunkData.devices.forEach((d, i) => { updatedList[insertAt + i] = d; });
                // Truncate array to the exact total count
                updatedList = updatedList.slice(0, chunkData.total_count);
                return updatedList;
            });
            setTotalCount(chunkData.total_count);
        }
    }, [chunkData, currentChunk, isPlaceholderData, activeFilters]);

    useEffect(() => {
        const urlQ = searchParams.get("q") || "";
        const urlPage = parseInt(searchParams.get("page") || "0");

        // Only update if the URL 'q' is different from our active filter 'q'
        if (urlQ !== activeFilters.q) {
            const newFilters = parseUrlToFilters(); // Re-parse everything from URL

            setAllFetchedDevices([]); // Wipe buffer for new search
            setCurrentChunk(0);
            setCurrentPage(urlPage);

            setActiveFilters(newFilters);
            setPendingFilters(newFilters);
        } else if (urlPage !== currentPage) {
            // Handle case where only page changed
            setCurrentPage(urlPage);
        }
    }, [activeFilters.q, currentPage, parseUrlToFilters, searchParams]);

    // Handle Page Change
    async function handlePageChange(newPage: number) {
        const startItemIndex = newPage * DEVICES_PER_PAGE;
        const endItemIndex = startItemIndex + DEVICES_PER_PAGE - 1;
        if (!allFetchedDevices[startItemIndex]) {
            setCurrentChunk(Math.floor(startItemIndex / CHUNK_SIZE));
        } else if (!allFetchedDevices[endItemIndex] && endItemIndex < totalCount) {
            setCurrentChunk(Math.floor(endItemIndex / CHUNK_SIZE));
        }
        setCurrentPage(newPage);
        syncFiltersToUrl(activeFilters, newPage);
        setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 10);
    }

    // Apply Filters
    function applyFilters(overrideFilters?: DeviceFilters) {
        const filtersToApply = overrideFilters || pendingFilters;
        console.log("Applying Filters:", filtersToApply);
        setAllFetchedDevices([]);
        setCurrentChunk(0);
        setCurrentPage(0);

        // Sync both states to match
        setActiveFilters(filtersToApply);
        setPendingFilters(filtersToApply);

        setClosePopover(true);
        syncFiltersToUrl(filtersToApply, 0);

        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }, 10);
    }

    // Clear Filters
    function handleClearFilter() {
        const resetFilters = { q: searchParams.get("q") || "" };
        setAllFetchedDevices([]);
        setCurrentChunk(0);
        setCurrentPage(0);
        setTotalCount(0);
        setActiveFilters(resetFilters);
        setPendingFilters(resetFilters);
        setClosePopover(true);
        syncFiltersToUrl(resetFilters, 0); // Clear from URL
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }, 10);
    }

    // handleFilterChange logic remains identical to your previous working version...
    function handleFilterChange(
        filter: keyof DeviceFilters,
        value: string | number[] | boolean,
        e: React.ChangeEvent<HTMLInputElement> | null
    ) {
        // 1. POSITIONING LOGIC
        if (e && e.target && filterRef.current) {
            setClosePopover(false);
            const row = (e.target as HTMLElement).closest("div");
            const containerRect = filterRef.current.getBoundingClientRect();

            if (row) {
                const rowRect = row.getBoundingClientRect();
                setPopoverPos({
                    top: rowRect.top - containerRect.top,
                    right: window.innerWidth - containerRect.right + 330
                });
            }
        }
        console.log(closePopover, popoverPos)

        // 2. SAFETY GUARD: Detect if 'value' is actually an event
        // React events have a 'nativeEvent' or 'target' property.
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            console.error(`Blocked circular structure leak in filter: ${filter}`);
            return pendingFilters;
        }

        // 3. STATE UPDATE LOGIC
        const nextState: DeviceFilters = { ...pendingFilters };

        if (typeof value === "boolean") {
            if (value) nextState[filter] = true as any;
            else delete nextState[filter];
        }
        else if (typeof value === "string") {
            const existing = (nextState[filter] as string[]) || [];
            const newArr = existing.includes(value)
                ? existing.filter(v => v !== value)
                : [...existing, value];

            if (newArr.length === 0) delete nextState[filter];
            else (nextState[filter] as any) = newArr;
        }
        else if (Array.isArray(value)) {
            // Ensure we are only storing numbers, not objects/events
            const existing = (nextState[filter] as number[][]) || [];
            const index = existing.findIndex(r => r[0] === value[0] && r[1] === value[1]);
            const newArr = index >= 0
                ? existing.filter((_, i) => i !== index)
                : [...existing, value];

            if (newArr.length === 0) delete nextState[filter];
            else (nextState[filter] as any) = newArr;
        }

        setPendingFilters(nextState);
        return nextState;
    }

    return {
        data: allFetchedDevices,
        isLoading,
        error,
        currentTableData: allFetchedDevices.slice(currentPage * DEVICES_PER_PAGE, (currentPage + 1) * DEVICES_PER_PAGE),
        currentPage,
        maxPage: Math.ceil(totalCount / DEVICES_PER_PAGE) || 1,
        totalCount,
        pendingFilters,
        filteredDevicesCount: countData?.count ?? 0,
        availableBrands: countData?.available_brands ?? [],
        popoverPos,
        isApplyBarVisible: !closePopover,
        filterRef,
        activeFilters,
        handlePageChange,
        handleFilterChange,
        applyFilters,
        handleClearFilter,
        closeApplyBar: () => setClosePopover(true),
    };
}